import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import is, { isObject } from '@sindresorhus/is';
import type {
  DuplicateAttachmentFile,
  OversizedFile,
} from '@snap-types/actions/send-snap-attachments';
import { isAxiosError } from 'axios';
import type { $ZodErrorTree } from 'zod/v4/core';

export type ApiResponseError = {
  status: number;
  data: {
    code: string;
    statusCode: string;
    error: string;
    message: string;
    detail?: string;
    validationErrors?: $ZodErrorTree<unknown>;
    oversizedFiles?: OversizedFile[];
    fileSizeLimitInBytes?: number;
    duplicateFiles?: DuplicateAttachmentFile[];
    metadata?: Record<string, unknown>;
  };
};

/**
 * Helper for extracting error response data based on the general shape of the
 * API's `ApplicationError.toResponse()` method.
 *
 * Based on advice from https://redux-toolkit.js.org/rtk-query/usage-with-typescript#type-safe-error-handling.
 */
export function isApiResponseError(error: unknown): error is ApiResponseError {
  return (
    is.plainObject(error) &&
    'status' in error &&
    isObject(error.data) &&
    'message' in error.data &&
    'error' in error.data &&
    'detail' in error.data
  );
}

export function extractApiResponseErrorMessage(
  error: Error | FetchBaseQueryError | SerializedError | undefined,
  fallbackMessage = 'An unknown error occurred',
) {
  if (!error) {
    return fallbackMessage;
  }

  if (isApiResponseError(error)) {
    return error.data.message;
  }

  if (isAxiosError(error)) {
    return error.response?.data.message ?? fallbackMessage;
  }

  return fallbackMessage;
}
